import { useCallback, useEffect, useRef, useState } from 'react';
import { createJob, getJob, getWorkerStatus, JobType, RefinitivJob, WorkerStatus } from '../../../services/refinitivService';

/** Starts a Refinitiv job and polls it until it's done / failed. */
export function useRefinitivJob<R = any>() {
  const [job, setJob] = useState<RefinitivJob<R> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  const stop = () => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = null;
  };
  useEffect(() => stop, []);

  const poll = useCallback((id: number) => {
    timer.current = window.setTimeout(async () => {
      try {
        const j = await getJob<R>(id);
        setJob(j);
        if (j.status === 'queued' || j.status === 'running') poll(id);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    }, 2000);
  }, []);

  const start = useCallback(async (type: JobType, params: Record<string, unknown>) => {
    stop();
    setError(null);
    setJob(null);
    try {
      const j = await createJob(type, params);
      setJob(j as RefinitivJob<R>);
      poll(j.id);
      return j;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return null;
    }
  }, [poll]);

  const reset = useCallback(() => { stop(); setJob(null); setError(null); }, []);
  const busy = !!job && (job.status === 'queued' || job.status === 'running');
  return { job, error: error ?? (job?.status === 'failed' ? job.error : null), busy, start, reset };
}

/** Worker online/offline, refreshed every 20 s. */
export function useWorkerStatus() {
  const [status, setStatus] = useState<WorkerStatus | null>(null);
  useEffect(() => {
    let alive = true;
    const load = () => getWorkerStatus().then((s) => alive && setStatus(s)).catch(() => alive && setStatus(null));
    load();
    const t = window.setInterval(load, 20_000);
    return () => { alive = false; window.clearInterval(t); };
  }, []);
  return status;
}

export const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
